import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { ApiError, apiErrorFromNetwork, apiErrorFromResponse } from './errors';

/**
 * Same-origin, always. In prod Caddy serves the SPA at `/` and the API at
 * `/api`; in dev the Vite proxy reproduces that (see vite.config.ts). The
 * `pc_session` cookie is HttpOnly, Secure, SameSite=Lax, Path=/api — the
 * browser attaches it on its own.
 *
 * There is nothing to read, store or refresh here. No `Authorization` header,
 * no CORS, no token flow. If a change to this file starts to need one,
 * something upstream has been misunderstood.
 *
 * Note the empty base URL: the served document already carries the `/api`
 * prefix on every path, so call sites read `/api/auth/me`. Setting a base of
 * `/api` here would double it.
 */
const BASE_URL = typeof window === 'undefined' ? '' : window.location.origin;

/*
 * Resolved against the current origin rather than left as a bare `/api` prefix.
 * For a same-origin SPA the two are equivalent — the browser resolves one into
 * the other — but stating the origin makes the client usable anywhere a
 * `Request` has to be constructed up front, which openapi-fetch does before it
 * ever calls fetch. A bare relative URL throws there outside a browser.
 */

/**
 * Turns every non-2xx into a typed `ApiError` before it reaches a caller, so
 * no component ever branches on a raw status code and the 403/503 distinction
 * cannot be lost by accident.
 */
const throwTypedErrors: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return response;
    throw await apiErrorFromResponse(response.clone());
  },
  onError({ error }) {
    // Fetch itself rejected — offline, DNS, TLS, VPN down. Never reached the API.
    throw apiErrorFromNetwork(error);
  },
};

export const api = createClient<paths>({
  baseUrl: BASE_URL,
  // Belt and braces: same-origin requests send cookies anyway, but stating it
  // means a future move to a different origin fails loudly rather than by
  // silently dropping the session.
  credentials: 'same-origin',
});

api.use(throwTypedErrors);

/**
 * Contract drift, reported where it happens rather than three screens later.
 *
 * Every call site narrows `data` with an unchecked `as`. Nothing verifies that
 * the payload is the shape the generated type claims, so when the contract and
 * the server disagree the first symptom is a component rendering `undefined` —
 * or throwing — a long way from the response that caused it. That is how the
 * owner typeahead crashed this week: `field is string` asserted over a value
 * the API had stopped sending.
 *
 * This compares a successful body against the schema the contract declares for
 * that operation and names the fields that disagree. It is a report and nothing
 * else. It never throws, never touches the response, and the caller receives
 * exactly what it would have received without it — a console that refused to
 * render because a field it does not use went missing would be a worse bug than
 * the drift it was detecting.
 *
 * Dev and test only, and structurally so rather than by a runtime check. Vite
 * substitutes a literal `false` for `import.meta.env.DEV` in a production
 * build, which makes this whole block dead code and takes the only reference to
 * `./conformance` with it. That reference is the point: `conformance.ts`
 * statically imports `openapi.json`, all 616 kB of it, so a *static* import
 * here would put the entire contract document in the production bundle. Inside
 * the branch and dynamic, it is not merely unreachable: no chunk is emitted for
 * it at all, and the built output contains no occurrence of `conformance` nor
 * any string from the document.
 *
 * Measured, because this is the one claim in the file that must not be taken on
 * trust. Against the same build without it, the console's JavaScript goes from
 * 436,647 to 437,176 bytes and the portal's from 332,298 to 332,301. The 529
 * bytes are not this code — none of it survives — but a chunking side effect:
 * a dynamic import anywhere in the module makes Rolldown split its interop
 * helpers into a shared `rolldown-runtime` chunk instead of inlining them.
 * Hoisting the import out of the handler does not change that. If the number
 * above ever moves by more than that, something is shipping that should not be.
 */
if (import.meta.env.DEV) {
  const reportContractDrift: Middleware = {
    onResponse({ request, response, schemaPath }) {
      // Failure envelopes belong to the error taxonomy, not to the contract's
      // success schema. Note this runs *before* `throwTypedErrors`, which is
      // why the non-2xx case has to be turned away here rather than relying on
      // that middleware having thrown: openapi-fetch walks `onResponse` in
      // reverse registration order, so the last registered runs first.
      if (!response.ok) return undefined;
      if (!(response.headers.get('content-type') ?? '').includes('application/json')) {
        return undefined;
      }

      // Taken now, synchronously, because a clone is only available while the
      // body is still unread — a moment later openapi-fetch consumes it.
      const copy = response.clone();

      /*
       * Deliberately not awaited, which is the difference between an observer
       * and a change in behaviour.
       *
       * openapi-fetch awaits `onResponse`, so anything done here happens with
       * the caller waiting — and reading a body and loading a module are not
       * free. Awaited, this delayed every response in the app by enough to move
       * a query's resolution past the render that was waiting on it: three
       * tests elsewhere in the suite went red, all of them asserting on state
       * that arrives one tick after a response, none of them anything to do
       * with drift. That is exactly the class of harm this must not cause. So
       * the check reads a copy on its own time and the response goes back
       * immediately, unchanged and unslowed. A report is a console message; it
       * does not matter that it lands a tick late.
       */
      void (async () => {
        try {
          const body: unknown = await copy.json();

          // `schemaPath` is the path template openapi-fetch was called with —
          // `/api/lxc/{vmid}`, not `/api/lxc/141` — which is exactly the key
          // the contract document is written under. Nothing is reconstructed.
          const { conformanceReport, responseSchema } = await import('./conformance');

          // The concrete status rather than the operation's default 2xx: we
          // know which response arrived, so it is checked against the schema
          // declared for that one. A 2xx the contract does not declare has no
          // schema to disagree with, and is left alone.
          const schema = responseSchema(schemaPath, request.method, String(response.status));
          if (!schema) return;

          const report = conformanceReport(schema, body, `${request.method} ${schemaPath}`);
          if (report !== undefined) console.error(report);
        } catch {
          // A fault in the check must never become a fault in the app, and must
          // not become noise either — a body that is not the JSON its header
          // promised would otherwise report itself on every single response.
        }
      })();

      // Always: returning a Response here would replace the caller's.
      return undefined;
    },
  };

  api.use(reportContractDrift);
}

/** Narrowing helper for `catch` blocks and query `error` values. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
